import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import './AdminPollData.css';

const AdminPollData = () => {
  const { t } = useTranslation();
  
  // Phase dates mapping (Bihar Election 2025)
  const PHASE_DATES = {
    1: '2025-11-06',  // Phase 1: 06.11.2025
    2: '2025-11-11'
  };

  const [formData, setFormData] = useState({
    constituencyId: '',
    phaseNumber: 1,
    pollDate: PHASE_DATES[1],
    overallPercentage: '',
    malePercentage: '',
    femalePercentage: '',
    totalVoters: '',
    maleVoters: '',
    femaleVoters: '',
    reportedBy: ''
  });

  const [pollDataList, setPollDataList] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(1);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadConstituencies();
  }, []);

  useEffect(() => {
    loadPollData();
  }, [selectedPhase, page]);

  // ✅ FIXED: Remove /api prefix - apiCall() adds base URL
  const loadConstituencies = async () => {
    try {
      console.log('📍 Loading constituencies...');
      const response = await api.getAllConstituencies();
      console.log('✅ Constituencies loaded:', response);
      setConstituencies(response.data || response || []);
    } catch (err) {
      console.error('❌ Error loading constituencies:', err);
      setError('Failed to load constituencies. Please check if backend is running.');
    }
  };

  // ✅ FIXED: Use api.getPollDataByPhase() method instead of direct endpoint
  const loadPollData = async () => {
    try {
      setLoading(true);
      console.log(`📊 Loading poll data for phase ${selectedPhase}...`);
      
      const response = await api.getPollDataByPhase(selectedPhase, page, 10);
      console.log('✅ Poll data loaded:', response);
      
      setPollDataList(response.data || response || []);
    } catch (err) {
      console.error('❌ Error loading poll data:', err);
      setError('Failed to load poll data');
    } finally {
      setLoading(false);
    }
  };

  const handlePhaseChange = (e) => {
    const newPhase = parseInt(e.target.value);
    setFormData(prev => ({
      ...prev,
      phaseNumber: newPhase,
      pollDate: PHASE_DATES[newPhase]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setLoading(true);

      // Validate form data
      if (!formData.constituencyId) {
        setError('❌ Please select a constituency');
        setLoading(false);
        return;
      }

      if (!formData.overallPercentage || !formData.malePercentage || !formData.femalePercentage) {
        setError('❌ Please fill in all percentage fields');
        setLoading(false);
        return;
      }

      // ✅ Use api.validatePollData() if available
      const validation = api.validatePollData?.(formData);
      if (validation && !validation.isValid) {
        setError(`❌ ${validation.errors.join(', ')}`);
        setLoading(false);
        return;
      }

      const payload = {
        constituencyId: parseInt(formData.constituencyId),
        phaseNumber: parseInt(formData.phaseNumber),
        pollDate: formData.pollDate,
        overallPercentage: parseFloat(formData.overallPercentage),
        malePercentage: parseFloat(formData.malePercentage),
        femalePercentage: parseFloat(formData.femalePercentage),
        totalVoters: parseInt(formData.totalVoters) || 0,
        maleVoters: parseInt(formData.maleVoters) || 0,
        femaleVoters: parseInt(formData.femaleVoters) || 0,
        reportedBy: formData.reportedBy?.trim() || 'Admin'
      };

      console.log('📤 Submitting poll data:', payload);
      
      // ✅ Use api.submitPollData() method
      const result = await api.submitPollData(payload);
      
      console.log('✅ Poll data submitted:', result);

      setSuccess('✅ Poll data submitted successfully!');
      
      // Reset form
      setFormData({
        constituencyId: '',
        phaseNumber: selectedPhase,
        pollDate: PHASE_DATES[selectedPhase],
        overallPercentage: '',
        malePercentage: '',
        femalePercentage: '',
        totalVoters: '',
        maleVoters: '',
        femaleVoters: '',
        reportedBy: ''
      });

      // Reload data
      loadPollData();
    } catch (err) {
      console.error('❌ Submission error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Error submitting poll data';
      setError(`❌ ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this poll data?')) return;

    try {
      setLoading(true);
      console.log(`🗑️ Deleting poll data: ${id}`);
      
      // ✅ Use api.deletePollData() method
      await api.deletePollData(id);
      
      setSuccess('✅ Poll data deleted successfully!');
      loadPollData();
    } catch (err) {
      console.error('❌ Delete error:', err);
      setError('❌ Error deleting poll data');
    } finally {
      setLoading(false);
    }
  };

  const getPhaseDisplayDate = (phaseNumber) => {
    const dateStr = PHASE_DATES[phaseNumber];
    if (!dateStr) return 'N/A';
    
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'numeric', 
      year: 'numeric' 
    });
  };

  const getConstituencyName = (constituencyId) => {
    const constituency = constituencies.find(c => c.constituencyId === parseInt(constituencyId));
    return constituency ? constituency.constituencyName : 'N/A';
  };

  return (
    <div className="admin-poll-data">
      <h2>📊 Poll Data Management</h2>

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="close-alert">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="close-alert">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* Add Poll Data Form */}
      <div className="form-section">
        <h3>📝 Add/Update Poll Data</h3>
        <form onSubmit={handleSubmit} className="poll-form">
          <div className="form-grid">
            {/* Constituency Selection */}
            <div className="form-group">
              <label>Constituency *</label>
              <select
                name="constituencyId"
                value={formData.constituencyId}
                onChange={handleInputChange}
                required
              >
                <option value="">🔍 Select Constituency</option>
                {constituencies.length > 0 ? (
                  constituencies.map(c => (
                    <option key={c.constituencyId} value={c.constituencyId}>
                      {c.constituencyName}
                    </option>
                  ))
                ) : (
                  <option disabled>Loading constituencies...</option>
                )}
              </select>
            </div>

            {/* Phase Selection with Auto-Date */}
            <div className="form-group">
              <label>Phase Number *</label>
              <select
                name="phaseNumber"
                value={formData.phaseNumber}
                onChange={handlePhaseChange}
                required
              >
                {[1, 2].map(p => (
                  <option key={p} value={p}>
                    Phase {p} - {getPhaseDisplayDate(p)}
                  </option>
                ))}
              </select>
              <small className="date-info">
                📅 Date automatically set: {getPhaseDisplayDate(formData.phaseNumber)}
              </small>
            </div>

            {/* Poll Date (Read-only) */}
            <div className="form-group">
              <label>Poll Date (Auto-set) *</label>
              <input
                type="date"
                name="pollDate"
                value={formData.pollDate}
                onChange={handleInputChange}
                disabled
                style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
              />
            </div>

            {/* Overall Percentage */}
            <div className="form-group">
              <label>Overall Percentage (0-100) *</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  name="overallPercentage"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.overallPercentage}
                  onChange={handleInputChange}
                  placeholder="e.g., 65.74"
                  required
                />
                <span className="unit">%</span>
              </div>
            </div>

            {/* Male Percentage */}
            <div className="form-group">
              <label>Male Percentage (0-100) *</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  name="malePercentage"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.malePercentage}
                  onChange={handleInputChange}
                  placeholder="e.g., 68.50"
                  required
                />
                <span className="unit">%</span>
              </div>
            </div>

            {/* Female Percentage */}
            <div className="form-group">
              <label>Female Percentage (0-100) *</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  name="femalePercentage"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.femalePercentage}
                  onChange={handleInputChange}
                  placeholder="e.g., 62.90"
                  required
                />
                <span className="unit">%</span>
              </div>
            </div>

            {/* Total Voters */}
            <div className="form-group">
              <label>Total Voters</label>
              <input
                type="number"
                name="totalVoters"
                min="0"
                value={formData.totalVoters}
                onChange={handleInputChange}
                placeholder="e.g., 500000"
              />
            </div>

            {/* Male Voters */}
            <div className="form-group">
              <label>Male Voters</label>
              <input
                type="number"
                name="maleVoters"
                min="0"
                value={formData.maleVoters}
                onChange={handleInputChange}
                placeholder="e.g., 250000"
              />
            </div>

            {/* Female Voters */}
            <div className="form-group">
              <label>Female Voters</label>
              <input
                type="number"
                name="femaleVoters"
                min="0"
                value={formData.femaleVoters}
                onChange={handleInputChange}
                placeholder="e.g., 250000"
              />
            </div>

            {/* Reported By */}
            <div className="form-group">
              <label>Reported By</label>
              <input
                type="text"
                name="reportedBy"
                value={formData.reportedBy}
                onChange={handleInputChange}
                placeholder="Your name"
              />
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            <i className={`fas fa-${loading ? 'spinner fa-spin' : 'paper-plane'}`}></i>
            {loading ? 'Submitting...' : 'Submit Poll Data'}
          </button>
        </form>
      </div>

      {/* Poll Data List */}
      <div className="list-section">
        <h3>📋 Poll Data for Phase {selectedPhase}</h3>

        {/* Phase Filter Buttons */}
        <div className="phase-filter">
          {[1, 2].map(p => (
            <button
              key={p}
              className={`phase-btn ${selectedPhase === p ? 'active' : ''}`}
              onClick={() => {
                setSelectedPhase(p);
                setPage(1);
              }}
            >
              <span className="phase-num">Phase {p}</span>
              <span className="phase-date">{getPhaseDisplayDate(p)}</span>
            </button>
          ))}
        </div>

        {/* Data Table */}
        {loading ? (
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading poll data...</p>
          </div>
        ) : (
          <div className="poll-table-container">
            {pollDataList.length > 0 ? (
              <table className="poll-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Constituency</th>
                    <th>Overall %</th>
                    <th>Male %</th>
                    <th>Female %</th>
                    <th>Total Voters</th>
                    <th>Reported By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pollDataList.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="index">{(page - 1) * 10 + idx + 1}</td>
                      <td className="constituency-name">
                        {item.constituency || getConstituencyName(item.constituencyId) || 'N/A'}
                      </td>
                      <td>
                        <span className="percentage-badge overall">
                          {item.overallPercentage}%
                        </span>
                      </td>
                      <td>
                        <span className="male-badge">
                          <i className="fas fa-mars"></i> {item.malePercentage}%
                        </span>
                      </td>
                      <td>
                        <span className="female-badge">
                          <i className="fas fa-venus"></i> {item.femalePercentage}%
                        </span>
                      </td>
                      <td className="voters">
                        {item.totalVoters > 0 ? item.totalVoters.toLocaleString() : '-'}
                      </td>
                      <td className="reported-by">{item.reportedBy || '-'}</td>
                      <td>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(item.id)}
                          title="Delete this poll data"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">
                <i className="fas fa-inbox"></i>
                <p>No poll data found for Phase {selectedPhase}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPollData;
